import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import { format } from 'date-fns';

const CATEGORY_STYLES = {
  Feature: 'bg-emerald-100 text-emerald-700',
  Improvement: 'bg-violet-100 text-violet-700',
  Fix: 'bg-amber-100 text-amber-700',
  Breaking: 'bg-rose-100 text-rose-700',
};

const STORAGE_KEY = 'whats_new_modal_dismissed_at';
const RESHOW_AFTER_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Auto-shows on first session per user when there are unread What's New entries.
 * Triggered globally from any authenticated layout.
 */
export default function WhatsNewModal() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const userKey = user?.id ? `${STORAGE_KEY}:${user.id}` : null;

  const dismiss = useCallback(async (markRead = false) => {
    setOpen(false);
    if (userKey) localStorage.setItem(userKey, String(Date.now()));
    if (markRead && entries.length) {
      try {
        await api.post('/whats-new/mark-read', { entry_ids: entries.map(e => e.id) });
        window.dispatchEvent(new CustomEvent('whats-new-read'));
      } catch {}
    }
  }, [userKey, entries]);

  useEffect(() => {
    if (!user || !userKey) return;
    if (user.role === 'course_qa') return;
    // Don't show on the What's New page itself or login page
    if (location.pathname.includes('whats-new') || location.pathname === '/login') return;
    const lastDismissed = parseInt(localStorage.getItem(userKey) || '0', 10);
    if (Date.now() - lastDismissed < RESHOW_AFTER_MS) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/whats-new');
        const unread = (res.data.entries || []).filter(e => !e.is_read);
        if (!cancelled && unread.length > 0) {
          setEntries(unread.slice(0, 5));
          setOpen(true);
        }
      } catch {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;

  const handleViewAll = async () => {
    await dismiss(false);
    // Pick the right path based on which layout user is in
    const target = location.pathname.startsWith('/training') ? '/training/whats-new' : '/whats-new';
    navigate(target);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && dismiss(true)}>
      <DialogContent data-testid="whats-new-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </span>
            <div>
              <p className="text-base font-bold">What's new</p>
              <p className="text-xs font-normal text-muted-foreground">{entries.length} update{entries.length === 1 ? '' : 's'} since you last checked in</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto -mx-1 px-1">
          {entries.map(e => (
            <div key={e.id} className="border border-slate-100 rounded-sm p-3 bg-slate-50/40">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`rounded-sm text-[10px] px-1.5 py-0 hover:opacity-90 ${CATEGORY_STYLES[e.category] || 'bg-slate-100 text-slate-700'}`}>{e.category}</Badge>
                {e.module && <Badge variant="outline" className="rounded-sm text-[10px]">{e.module}</Badge>}
                <span className="ml-auto text-[10px] text-slate-400 font-data">
                  {e.published_at && format(new Date(e.published_at), 'dd MMM')}
                </span>
              </div>
              <p className="text-sm font-semibold text-slate-800 mt-1.5">{e.title}</p>
              {e.description && <p className="text-[12px] text-slate-600 mt-1 line-clamp-2">{e.description}</p>}
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => dismiss(false)} data-testid="modal-dismiss" className="rounded-sm">
            <X className="w-3.5 h-3.5 mr-2" /> Later
          </Button>
          <Button onClick={handleViewAll} data-testid="modal-view-all" className="rounded-sm bg-violet-600 hover:bg-violet-700">
            View all updates <ArrowRight className="w-3.5 h-3.5 ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
